import {
    ChangeEvent as ReactChangeEvent,
    CSSProperties,
    FocusEvent as ReactFocusEvent,
    KeyboardEvent as ReactKeyboardEvent,
    MouseEvent as ReactMouseEvent,
    ReactNode,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Search, X } from 'lucide-react'
import { cn } from 'utils/cn'
import { POPOVER_Z_INDEX } from './z-index'
import { AutocompleteRow, buildRows } from './Autocomplete.utils'
import { AutocompleteOptionsList } from './Autocomplete.OptionsList'

/** The panel's height cap, before the viewport's own limit. */
const PANEL_MAX_HEIGHT = 360

export interface AutocompleteOption {
    label: string
    value: string
    disabled?: boolean
    title?: string
    trailingContent?: ReactNode
    searchText?: string
    /**
     * Optional section header this option renders under (e.g. "Truck" / "Trailer"). Purely
     * additive — an options array where no option sets `group` renders exactly as it did before
     * grouping existed: a flat list, no headers. Group order follows each group's first
     * appearance in `options`, so the caller controls ordering by how it arranges the array.
     */
    group?: string
}

export interface AutocompleteProps {
    variant?: 'primary' | 'secondary'
    label?: string
    required?: boolean
    placeholder?: string
    options: AutocompleteOption[]
    multiple?: boolean
    value?: string[]
    defaultValue?: string[]
    onChange?: (values: string[]) => void
    disabled?: boolean
    emptyMessage?: string
    style?: CSSProperties
    /**
     * Single-select only: render the same button trigger and in-popover search field `multiple`
     * uses, instead of the editable combobox trigger. For a single picker that sits beside a
     * multi-select one (a vehicle's drivers and owner next to its dispatchers), so both open and
     * search the same way. Ignored when `multiple` is set — that mode already searches this way.
     */
    searchInPanel?: boolean
    /**
     * Single-select: typing highlights the first match, so Enter picks it without an arrow key first. Off by default —
     * every other picker keeps Enter picking only what the arrow keys highlighted. On for the Box Truck driver form's
     * Owner and Truck pickers.
     */
    selectFirstMatchOnEnter?: boolean
    /**
     * Optional pinned row rendered below the (filtered) options list — always visible regardless
     * of the search query and never treated as a selectable value. Used for actions like "Quick
     * Add" that must stay reachable even when the current search text matches nothing.
     */
    trailingAction?: { label: string; icon?: ReactNode; onClick: () => void }
    /**
     * Show counts for grouped options: each group header gets its option count (`EXPENSE (42)`) and a sticky line
     * at the top of the list totals them (`7 types · 213 accounts`). Counts follow the search.
     */
    groupSummary?: { groupNoun: string; optionNoun: string }
    /**
     * Single-select only: an icon button beside the clear ✕ while a value is chosen, acting on that value
     * (e.g. editing the vehicle a driver is on without leaving the driver's form). Ignored with `multiple`.
     */
    selectionAction?: { label: string; icon: ReactNode; onClick: (value: string) => void }
    /**
     * The options are a page the caller searches on the server (a team's drivers or members, never read whole): every
     * change of the typed query is reported here, and the options are listed as given rather than filtered again here,
     * since the server matches on more than the label (an email, a phone).
     */
    onSearchChange?: (query: string) => void
    /** More options match than are listed: scrolling to the list's end calls this to read the next page. */
    onLoadMore?: () => void
}

/** How close to the list's end (px) a scroll asks for the next page. */
const LOAD_MORE_THRESHOLD = 48

export const Autocomplete = ({
    variant = 'primary',
    label,
    required = false,
    placeholder = 'Animals',
    options,
    multiple = false,
    value: controlledValue,
    defaultValue = [],
    onChange,
    disabled = false,
    emptyMessage = 'No results found',
    style,
    searchInPanel = false,
    selectFirstMatchOnEnter = false,
    trailingAction,
    groupSummary,
    selectionAction,
    onSearchChange,
    onLoadMore,
}: AutocompleteProps) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [selected, setSelected] = useState<string[]>(defaultValue)
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
    // Pinned by the edge that faces the trigger: `top` below it, `bottom` above it. The direction is chosen once at
    // open from the full list's height; typing then shrinks the list, and a panel above that kept its `top` was left
    // floating far over the field (over the card above it) with "No results found" or a single row.
    const [panelPos, setPanelPos] = useState<{ top?: number; bottom?: number; maxHeight: number; left: number; width: number } | null>(null)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    // The editable trigger input (single-select). Already focused by the time the panel opens —
    // opening always happens THROUGH this element (focus/click/typing/arrow keys) — so nothing
    // needs to move focus into it programmatically.
    const inputRef = useRef<HTMLInputElement>(null)
    // The legacy in-popover search field, still used verbatim for `multiple` — see `editableTrigger`.
    const searchInputRef = useRef<HTMLInputElement>(null)
    const optionsRef = useRef<HTMLDivElement>(null)
    // Set by an arrow key, read by the effect that follows the highlight: only a KEYBOARD move scrolls the
    // list. A hover sets the highlight too, and scrolling under the pointer would walk the list on its own.
    const scrollToHighlight = useRef(false)
    const inputId = useId()
    const panelId = `${inputId}-panel`

    const values = controlledValue ?? selected

    // Single-select only. A multi-select trigger renders selected values as tags; folding a live
    // search box into that tag layout gets messy fast (where does typed text live relative to the
    // tags?), so `multiple` keeps the original separate button-trigger + in-popover search field
    // below, entirely unchanged — this is a deliberate scoping call, not an oversight. A
    // single-select caller can opt into that same trigger with `searchInPanel`.
    const editableTrigger = !multiple && !searchInPanel
    // Arrow keys and Enter work on every single-select list, whichever field the typing happens in. `multiple`
    // never had them and is left as it was.
    const keyboardNav = !multiple

    const filtered = onSearchChange ? options : options.filter((opt) => `${opt.label} ${opt.searchText ?? ''}`.toLowerCase().includes(query.toLowerCase()))

    // The caller searches on the server: it hears every change of the query, wherever the query was typed or cleared.
    const onSearchChangeRef = useRef(onSearchChange)
    onSearchChangeRef.current = onSearchChange
    useEffect(() => {
        onSearchChangeRef.current?.(query)
    }, [query])
    const rows = buildRows(options, filtered, Boolean(groupSummary))
    const optionRows = rows.filter((row): row is Extract<AutocompleteRow, { type: 'option' }> => row.type === 'option' && !row.option.disabled)

    const handleSelect = useCallback(
        (val: string) => {
            if (options.find((option) => option.value === val)?.disabled) return
            let next: string[]
            if (multiple) {
                next = values.includes(val) ? values.filter((v) => v !== val) : [...values, val]
            } else {
                next = [val]
                setOpen(false)
                setQuery('')
                setHighlightedIndex(-1)
                // The panel's search field held focus and is about to unmount; hand it back to the
                // trigger so keyboard users stay on the field they just answered.
                if (!editableTrigger) triggerRef.current?.focus()
            }
            if (!controlledValue) setSelected(next)
            onChange?.(next)
        },
        [values, multiple, editableTrigger, controlledValue, onChange, options]
    )

    const handleRemoveTag = useCallback(
        (val: string, e: ReactMouseEvent) => {
            e.stopPropagation()
            const next = values.filter((v) => v !== val)
            if (!controlledValue) setSelected(next)
            onChange?.(next)
        },
        [values, controlledValue, onChange]
    )

    const handleClearAll = useCallback(
        (e: ReactMouseEvent) => {
            e.stopPropagation()
            if (!controlledValue) setSelected([])
            onChange?.([])
        },
        [controlledValue, onChange]
    )

    // Legacy open, used by the button trigger (`multiple` or `searchInPanel`) — does not touch `query`, matching the
    // behavior this component always had before the editable trigger existed.
    const openPanel = () => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) setAnchorRect(rect)
        setPanelPos(null)
        setOpen(true)
    }

    // Editable-trigger open — always starts the query blank. The alternative (prefilling the query
    // with the current selection's label) would immediately narrow the list to near-nothing on
    // open, since the filter would match against the full previous label. Starting blank means the
    // full option list is visible the instant the field opens; the previously selected label is
    // what the closed field showed a moment ago, and it comes right back if the user leaves without
    // picking anything (see closeAndRevert).
    const openFresh = (initialHighlight = -1) => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) setAnchorRect(rect)
        setPanelPos(null)
        setOpen(true)
        setQuery('')
        setHighlightedIndex(initialHighlight)
    }

    const closeAndRevert = () => {
        setOpen(false)
        setQuery('')
        setHighlightedIndex(-1)
    }

    const handleTriggerFocus = () => {
        if (disabled) return
        openFresh()
    }

    const handleTriggerChange = (e: ReactChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value)
        setHighlightedIndex(firstMatchHighlight(e.target.value))
        if (!open) {
            const rect = triggerRef.current?.getBoundingClientRect()
            if (rect) setAnchorRect(rect)
            setPanelPos(null)
            setOpen(true)
        }
    }

    // With `selectFirstMatchOnEnter`, typing highlights the first match so Enter picks it; an empty query highlights
    // nothing. Otherwise typing clears the highlight, as it always has. Single-select only, as the keys are.
    // It also brings that row into view: a list scrolled down before typing would otherwise light a row off-screen.
    const firstMatchHighlight = (nextQuery: string) => {
        if (!selectFirstMatchOnEnter || !keyboardNav || !nextQuery.trim()) return -1
        scrollToHighlight.current = true
        return 0
    }

    const moveHighlight = (step: 1 | -1) => {
        scrollToHighlight.current = true
        setHighlightedIndex((i) => (step === 1 ? Math.min(i + 1, optionRows.length - 1) : Math.max(i - 1, 0)))
    }

    // The in-popover search field of a single-select (`searchInPanel`): the same keys the editable trigger
    // answers, so the list can be walked and picked from without leaving the field.
    const handlePanelSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            setOpen(false)
            setQuery('')
            setHighlightedIndex(-1)
            // The field holding focus unmounts with the panel; without this focus falls to <body>.
            if (keyboardNav) triggerRef.current?.focus()
            return
        }
        if (!keyboardNav) return
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            moveHighlight(1)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            moveHighlight(-1)
        } else if (e.key === 'Enter') {
            e.preventDefault()
            const highlighted = optionRows[highlightedIndex]
            if (highlighted) handleSelect(highlighted.option.value)
        }
    }

    const handleTriggerKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!open) {
                openFresh(0)
                return
            }
            moveHighlight(1)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            if (!open) {
                openFresh(optionRows.length - 1)
                return
            }
            moveHighlight(-1)
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (!open) {
                openFresh()
                return
            }
            const highlighted = optionRows[highlightedIndex]
            if (highlighted) handleSelect(highlighted.option.value)
        } else if (e.key === 'Escape') {
            e.preventDefault()
            // Consume the Escape entirely: ds2 Modal listens for Escape on `document` and closes
            // itself, so without stopping propagation one keypress would close both the panel AND
            // the parent modal, discarding the user's in-progress form.
            e.stopPropagation()
            closeAndRevert()
        }
    }

    const handleTriggerBlur = (e: ReactFocusEvent<HTMLInputElement>) => {
        // Selecting an option moves focus to that option's <button>, inside the portaled panel —
        // don't close (and don't discard the query) when focus is just moving there; the option's
        // own click handler closes the panel itself once it fires.
        if (panelRef.current?.contains(e.relatedTarget as Node)) return
        closeAndRevert()
    }

    useEffect(() => {
        // Portaled to <body>, so the panel is no longer a DOM descendant of containerRef —
        // check both refs or a click inside the panel registers as "outside" and closes it.
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (containerRef.current?.contains(target)) return
            if (panelRef.current?.contains(target)) return
            setOpen(false)
            setQuery('')
            setHighlightedIndex(-1)
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        // The panel is portaled to <body> with a position computed once at open time, so it can't
        // track the trigger as the page (or any scrollable ancestor) scrolls — closing on scroll
        // avoids the panel visually drifting away from its trigger. Scroll events bubbling from
        // inside the panel itself (its own overflow-y:auto options list) must be ignored here,
        // otherwise scrolling through a long options list would immediately close the panel.
        if (!open) return
        const handleScroll = (e: Event) => {
            if (panelRef.current?.contains(e.target as Node)) return
            setOpen(false)
        }
        window.addEventListener('scroll', handleScroll, true)
        return () => window.removeEventListener('scroll', handleScroll, true)
    }, [open])

    // Runs before paint: measures the panel's real (content-driven) height against the anchor's
    // position and flips it above the trigger when there isn't room below, plus caps its height to
    // the viewport — a CSS-relative panel gets silently clipped whenever any ancestor between it
    // and the trigger has overflow:hidden (rounded-corner cards, scrollable wizard panes, etc.).
    useLayoutEffect(() => {
        if (!open || !anchorRect || !panelRef.current) return
        const panelHeight = panelRef.current.offsetHeight
        const spaceBelow = window.innerHeight - anchorRect.bottom
        const spaceAbove = anchorRect.top
        // Below whenever it fits whole: the panel sits 8px off the field and keeps 8px to the viewport edge.
        const below = spaceBelow >= panelHeight + 16 || spaceBelow >= spaceAbove
        const edge = below ? { top: anchorRect.bottom + 8 } : { bottom: window.innerHeight - anchorRect.top + 8 }
        const maxHeight = Math.min(PANEL_MAX_HEIGHT, (below ? spaceBelow : spaceAbove) - 16)
        setPanelPos({ ...edge, maxHeight, left: anchorRect.left, width: anchorRect.width })
    }, [open, anchorRect])

    // Keep the keyboard-highlighted option visible: the list caps its height and scrolls inside, so arrowing
    // through a long one would otherwise walk the highlight below the fold. The list's own scrollTop is moved
    // rather than calling scrollIntoView — that may scroll the page too, which the listener above reads as
    // "the trigger moved, close the panel".
    const highlightedValue = optionRows[highlightedIndex]?.option.value
    useEffect(() => {
        if (!scrollToHighlight.current) return
        scrollToHighlight.current = false
        const list = optionsRef.current
        const item = highlightedValue === undefined ? null : document.getElementById(`${panelId}-option-${highlightedValue}`)
        if (!list || !item) return
        const top = item.offsetTop - list.offsetTop
        if (top < list.scrollTop) list.scrollTop = top
        else if (top + item.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = top + item.offsetHeight - list.clientHeight
    }, [highlightedValue, panelId])

    // Only the legacy `multiple` popover needs its search field focused programmatically — the
    // editable trigger is already focused by construction (see `editableTrigger` above).
    useEffect(() => {
        if (open && !editableTrigger) {
            setTimeout(() => searchInputRef.current?.focus(), 0)
        }
    }, [open, editableTrigger])

    const selectedLabels = values.map((v) => options.find((o) => o.value === v)).filter(Boolean) as AutocompleteOption[]
    const hasSelections = selectedLabels.length > 0
    // Closed: shows the persisted selection (or nothing, falling back to the placeholder attribute).
    // Open: shows the live query — the selection "gives way" to it the instant the field opens.
    const triggerValue = open ? query : hasSelections ? selectedLabels[0].label : ''
    const highlightedOption = open ? optionRows[highlightedIndex] : undefined
    const buttonAriaLabel =
        !editableTrigger && label ? `${label}: ${hasSelections ? selectedLabels.map((option) => option.label).join(', ') : placeholder}` : undefined

    return (
        <div ref={containerRef} className="relative flex flex-col gap-2" style={style}>
            {label &&
                (editableTrigger ? (
                    <label htmlFor={inputId} className="text-sm font-medium leading-[1.43] text-foreground">
                        {label}
                        {/* aria-hidden: the asterisk is a visual cue only — "required" is already
                            conveyed to assistive tech via aria-required on the input, so it stays out
                            of the label's accessible name instead of being read as a literal "star". */}
                        {required && (
                            <span aria-hidden="true" className="ml-1 text-danger">
                                *
                            </span>
                        )}
                    </label>
                ) : (
                    <span className="text-sm font-medium leading-[1.43] text-foreground">
                        {label}
                        {required && (
                            <span aria-hidden="true" className="ml-1 text-danger">
                                *
                            </span>
                        )}
                    </span>
                ))}

            {/* Trigger */}
            <div
                ref={triggerRef}
                role={editableTrigger ? undefined : 'button'}
                tabIndex={editableTrigger ? undefined : disabled ? -1 : 0}
                aria-label={buttonAriaLabel}
                onClick={() => {
                    if (disabled) return
                    if (editableTrigger) {
                        inputRef.current?.focus()
                    } else if (open) {
                        setOpen(false)
                    } else {
                        openPanel()
                    }
                }}
                onKeyDown={
                    editableTrigger
                        ? undefined
                        : (e) => {
                              if (disabled) return
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  if (open) setOpen(false)
                                  else openPanel()
                              } else if (e.key === 'ArrowDown' && keyboardNav && !open) {
                                  e.preventDefault()
                                  openPanel()
                              }
                          }
                }
                className={cn(
                    'flex min-h-9 w-full items-center rounded-xl text-left focus-within:outline-none focus-within:ring-2 focus-within:ring-accent',
                    variant === 'secondary' ? 'bg-default' : 'bg-surface shadow-field',
                    disabled ? 'pointer-events-none cursor-not-allowed opacity-50' : editableTrigger ? 'cursor-text' : 'cursor-pointer'
                )}
            >
                {/* Tags / editable query + placeholder area */}
                <div className="flex min-w-0 flex-1 items-center overflow-hidden pl-3">
                    {editableTrigger ? (
                        <input
                            ref={inputRef}
                            id={inputId}
                            type="text"
                            autoComplete="off"
                            role="combobox"
                            aria-expanded={open}
                            aria-controls={panelId}
                            aria-autocomplete="list"
                            aria-activedescendant={highlightedOption ? `${panelId}-option-${highlightedOption.option.value}` : undefined}
                            aria-required={required}
                            aria-label={!label ? placeholder : undefined}
                            disabled={disabled}
                            value={triggerValue}
                            onChange={handleTriggerChange}
                            onFocus={handleTriggerFocus}
                            onKeyDown={handleTriggerKeyDown}
                            onBlur={handleTriggerBlur}
                            placeholder={placeholder}
                            className="min-w-0 flex-1 border-none bg-transparent py-2 text-sm leading-5 text-foreground outline-none placeholder:text-foreground-muted"
                        />
                    ) : !multiple ? (
                        <span className={cn('truncate py-2 text-sm leading-5', hasSelections ? 'text-foreground' : 'text-foreground-muted')}>
                            {hasSelections ? selectedLabels[0].label : placeholder}
                        </span>
                    ) : (
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 py-2">
                            {selectedLabels.map((opt) => (
                                <span
                                    key={opt.value}
                                    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-xl bg-default px-1 py-0.5 text-xs font-medium leading-[1.34] text-foreground"
                                >
                                    {opt.label}
                                    <span onClick={(e) => handleRemoveTag(opt.value, e)} className="flex cursor-pointer items-center text-foreground">
                                        <X size={16} strokeWidth={1.5} />
                                    </span>
                                </span>
                            ))}
                            <span className="whitespace-nowrap text-sm leading-5 text-foreground-muted">{hasSelections ? '' : placeholder}</span>
                        </div>
                    )}
                </div>

                {selectionAction && !multiple && hasSelections && (
                    <button
                        type="button"
                        // Neither the press nor the key may reach the trigger: either would open the panel under the action.
                        onMouseDown={(e) => e.preventDefault()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (editableTrigger) closeAndRevert()
                            else setOpen(false)
                            selectionAction.onClick(selectedLabels[0].value)
                        }}
                        aria-label={selectionAction.label}
                        title={selectionAction.label}
                        // `mr-2`: the ✕ after it has 4px of padding and the chevron 12px, so this keeps the three icons evenly spaced.
                        className="mr-2 flex shrink-0 cursor-pointer items-center rounded-md px-1 py-2 text-foreground-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        {selectionAction.icon}
                    </button>
                )}

                {/* Clear button — also rendered in single mode so optional values (e.g. a driver's
                    Trailer or Co-Driver) can be un-assigned; the MUI Autocomplete this replaced
                    always had a clear affordance. */}
                {hasSelections && (
                    <span
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleClearAll}
                        role="button"
                        aria-label="Clear selection"
                        className="flex shrink-0 cursor-pointer items-center px-1 py-2 text-foreground-muted"
                    >
                        <X size={16} strokeWidth={1.5} />
                    </span>
                )}

                {/* Chevron — for the editable trigger it has its own open/close handling (clicking it
                    while open must close, which a bare click bubbling to the input's focus-to-open
                    behavior can't express, since re-focusing an already-focused input is a no-op).
                    For `multiple` it's purely decorative and lets its click bubble to the trigger. */}
                <span
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                        if (!editableTrigger || disabled) return
                        e.stopPropagation()
                        if (open) closeAndRevert()
                        else inputRef.current?.focus()
                    }}
                    className="flex shrink-0 items-center px-3 py-2 text-foreground"
                >
                    <ChevronDown size={16} strokeWidth={1.5} className={cn('transition-transform duration-200', open && 'rotate-180')} />
                </span>
            </div>

            {/* Dropdown */}
            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        id={panelId}
                        style={{
                            position: 'fixed',
                            top: panelPos ? panelPos.top : anchorRect.bottom + 8,
                            bottom: panelPos?.bottom,
                            left: panelPos ? panelPos.left : anchorRect.left,
                            width: panelPos ? panelPos.width : anchorRect.width,
                            maxHeight: panelPos ? panelPos.maxHeight : Math.min(PANEL_MAX_HEIGHT, window.innerHeight - 16),
                            zIndex: POPOVER_Z_INDEX,
                            visibility: panelPos ? 'visible' : 'hidden',
                        }}
                        className="flex flex-col gap-0.5 overflow-hidden rounded-3xl bg-surface p-2 shadow-overlay backdrop-blur-[20px]"
                    >
                        {/* In-popover search field — `multiple` and `searchInPanel`. The editable trigger
                            covers this role for single-select, so it isn't rendered there. */}
                        {!editableTrigger && (
                            <div className="pb-1">
                                <div className="box-border flex h-9 items-center gap-1.5 rounded-xl border-2 border-solid border-accent bg-surface px-3">
                                    <Search size={16} strokeWidth={1.5} className="shrink-0 text-foreground-muted" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={query}
                                        aria-controls={keyboardNav ? panelId : undefined}
                                        aria-activedescendant={
                                            keyboardNav && highlightedOption ? `${panelId}-option-${highlightedOption.option.value}` : undefined
                                        }
                                        onChange={(e) => {
                                            setQuery(e.target.value)
                                            setHighlightedIndex(firstMatchHighlight(e.target.value))
                                        }}
                                        onKeyDown={handlePanelSearchKeyDown}
                                        placeholder="Search..."
                                        className="min-w-0 flex-1 border-none bg-transparent text-sm leading-5 text-foreground outline-none"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Options */}
                        <div
                            ref={optionsRef}
                            className="flex max-h-60 min-h-0 flex-col overflow-y-auto"
                            onScroll={
                                onLoadMore
                                    ? (e) => {
                                          const list = e.currentTarget
                                          if (list.scrollTop + list.clientHeight >= list.scrollHeight - LOAD_MORE_THRESHOLD) onLoadMore()
                                      }
                                    : undefined
                            }
                        >
                            {groupSummary && filtered.length > 0 && (
                                <div
                                    aria-hidden="true"
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="sticky top-0 z-20 flex h-6 shrink-0 items-center bg-surface px-3 text-xs font-medium text-foreground-muted"
                                >
                                    {rows.filter((row) => row.type === 'header').length} {groupSummary.groupNoun} · {filtered.length} {groupSummary.optionNoun}
                                </div>
                            )}
                            <AutocompleteOptionsList
                                rows={rows}
                                filteredCount={filtered.length}
                                emptyMessage={emptyMessage}
                                selectedValues={values}
                                panelId={panelId}
                                onSelect={handleSelect}
                                hasSummary={Boolean(groupSummary)}
                                highlightEnabled={keyboardNav}
                                highlightedIndex={highlightedIndex}
                                onHighlight={setHighlightedIndex}
                            />
                        </div>

                        {trailingAction && (
                            <div className="mt-0.5 border-t border-border pt-1">
                                <button
                                    type="button"
                                    onClick={() => {
                                        trailingAction.onClick()
                                        setOpen(false)
                                        setQuery('')
                                    }}
                                    // Same Safari mousedown-focus gap as the option buttons — without this,
                                    // "Quick Add" (ContractSettingsSection / EquipmentSettingsSection) would
                                    // silently do nothing there: the panel unmounts on mousedown before the
                                    // click that fires trailingAction.onClick ever lands.
                                    onMouseDown={(e) => e.preventDefault()}
                                    // The pointer on the action takes the highlight off the options, so one row is lit.
                                    onMouseMove={() => keyboardNav && highlightedIndex !== -1 && setHighlightedIndex(-1)}
                                    className="flex min-h-9 w-full items-center gap-2 rounded-[20px] px-3 py-1.5 text-left text-sm font-medium leading-[1.43] text-accent transition-colors duration-150 hover:bg-default"
                                >
                                    {trailingAction.icon}
                                    {trailingAction.label}
                                </button>
                            </div>
                        )}
                    </div>,
                    document.body
                )}
        </div>
    )
}
